import type { Editor } from '@tiptap/core';
import type { ReadingState } from '@narrativebench/editor-core';
import { Node as ProseMirrorNode, Mark, type Schema } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import type React from 'react';
import type * as Y from 'yjs';
import {
  clearSearchMatchHighlights,
  findAllFoldedSubstringRanges,
  normalizeForSearch,
  resolveDocAnchor,
  textsEqualForSearch,
} from '@narrativebench/editor-core';
import {
  collectMentionsInRange,
  getDocumentMarkdownText,
  getDocumentPlainText,
  getLeafNodePlainText,
  isMentionNode,
  segmentSuggestionTextWithMentions,
} from './document-plain-text';
import {
  hasInlineMarkdownMarkers,
  parseInlineMarkdown,
  stripInlineMarkdown,
  type InlineMarkdownMarks,
} from './inline-markdown';

export interface TextPosition {
  from: number;
  to: number;
  plainFrom?: number;
  plainTo?: number;
}

export interface FindTextPositionOptions {
  selectionRange?: { from: number; to: number } | null;
  minDocPos?: number;
}

export const getMountedEditorDom = (editor: Editor | null): HTMLElement | null => {
  if (!editor || editor.isDestroyed) {
    return null;
  }

  try {
    const dom = editor.view.dom;
    return dom instanceof HTMLElement ? dom : null;
  } catch {
    return null;
  }
};

type TextIndexMapping = {
  textIndex: number;
  docPos: number;
  atomEndDocPos?: number;
};

const clampDocRange = (
  doc: ProseMirrorNode,
  from: number,
  to: number,
): { from: number; to: number } | null => {
  const docSize = doc.content.size;
  const clampedFrom = Math.max(0, Math.min(from, docSize));
  const clampedTo = Math.max(0, Math.min(to, docSize));
  if (clampedFrom >= clampedTo) {
    return null;
  }
  return { from: clampedFrom, to: clampedTo };
};

const buildSelectionTextIndex = (
  doc: ProseMirrorNode,
  from: number,
  to: number,
): {
  accumulatedText: string;
  positionMapping: TextIndexMapping[];
} => {
  const positionMapping: TextIndexMapping[] = [];
  let textIndex = 0;
  let accumulatedText = '';
  let openTextblock = false;

  const clamped = clampDocRange(doc, from, to);
  if (!clamped) {
    return { accumulatedText, positionMapping };
  }

  const rangeFrom = clamped.from;
  const rangeTo = clamped.to;

  doc.nodesBetween(rangeFrom, rangeTo, (node: ProseMirrorNode, pos: number) => {
    if (node.type.isTextblock) {
      if (openTextblock) {
        accumulatedText += '\n';
        textIndex += 1;
      }
      openTextblock = true;
      return;
    }

    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i++) {
        const docPos = pos + i;
        if (docPos >= rangeFrom && docPos < rangeTo) {
          positionMapping.push({ textIndex, docPos });
          accumulatedText += node.text[i]!;
          textIndex += 1;
        }
      }
      return;
    }

    if (isMentionNode(node) && pos >= rangeFrom && pos < rangeTo) {
      const mentionText = getLeafNodePlainText(node);
      const atomEndDocPos = pos + node.nodeSize;
      for (let i = 0; i < mentionText.length; i++) {
        positionMapping.push({ textIndex, docPos: pos, atomEndDocPos });
        accumulatedText += mentionText[i]!;
        textIndex += 1;
      }
    }
  });

  return { accumulatedText, positionMapping };
};

type DocTextIndex = {
  accumulatedText: string;
  positionMapping: TextIndexMapping[];
  textNodes: Array<{ text: string; pos: number }>;
};

const buildDocTextIndexWithTextblockNewlines = (doc: ProseMirrorNode): DocTextIndex => {
  const textNodes: Array<{ text: string; pos: number }> = [];
  const positionMapping: TextIndexMapping[] = [];
  let textIndex = 0;
  let textblockCount = 0;
  let accumulatedText = '';

  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.type.isTextblock) {
      if (textblockCount > 0) {
        accumulatedText += '\n';
        textIndex += 1;
      }
      textblockCount += 1;
    }

    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i++) {
        positionMapping.push({ textIndex, docPos: pos + i });
        textIndex += 1;
      }
      accumulatedText += node.text;
      textNodes.push({ text: node.text, pos });
      return;
    }

    if (isMentionNode(node)) {
      const mentionText = getLeafNodePlainText(node);
      const atomEndDocPos = pos + node.nodeSize;
      for (let i = 0; i < mentionText.length; i++) {
        positionMapping.push({ textIndex, docPos: pos, atomEndDocPos });
        textIndex += 1;
      }
      accumulatedText += mentionText;
    }
  });

  return { accumulatedText, positionMapping, textNodes };
};

let cachedDocTextIndex: { doc: ProseMirrorNode; index: DocTextIndex } | null = null;

const getDocTextIndexWithTextblockNewlines = (doc: ProseMirrorNode): DocTextIndex => {
  if (cachedDocTextIndex?.doc === doc) {
    return cachedDocTextIndex.index;
  }
  const index = buildDocTextIndexWithTextblockNewlines(doc);
  cachedDocTextIndex = { doc, index };
  return index;
};

const resolveFoldedRangeToDocPosition = (
  doc: ProseMirrorNode,
  index: DocTextIndex,
  searchText: string,
  match: { start: number; end: number },
): TextPosition | null => {
  const fromDocPos = mapTextIndexToDocPos(
    index.accumulatedText,
    index.positionMapping,
    index.textNodes,
    match.start,
    false,
  );
  const toDocPos = mapTextIndexToDocPos(
    index.accumulatedText,
    index.positionMapping,
    index.textNodes,
    match.end,
    true,
  );
  if (fromDocPos === null || toDocPos === null) {
    return null;
  }

  let actualText = getDocumentPlainText(doc, fromDocPos, toDocPos);
  if (textsEqualForSearch(actualText, searchText)) {
    return { from: fromDocPos, to: toDocPos };
  }
  if (actualText.length < searchText.length) {
    const diff = searchText.length - actualText.length;
    if (toDocPos + diff <= doc.content.size) {
      const adjustedToDocPos = toDocPos + diff;
      actualText = getDocumentPlainText(doc, fromDocPos, adjustedToDocPos);
      if (textsEqualForSearch(actualText, searchText)) {
        return { from: fromDocPos, to: adjustedToDocPos };
      }
    }
  } else if (actualText.length > searchText.length) {
    if (actualText.endsWith('\n') && !searchText.endsWith('\n')) {
      const adjustedToDocPos = toDocPos - 1;
      if (adjustedToDocPos > fromDocPos) {
        actualText = getDocumentPlainText(doc, fromDocPos, adjustedToDocPos);
        if (textsEqualForSearch(actualText, searchText)) {
          return { from: fromDocPos, to: adjustedToDocPos };
        }
      }
    }
  }
  return null;
};

const findAllResolvedTextPositionsInDoc = (
  doc: ProseMirrorNode,
  searchText: string,
): TextPosition[] => {
  if (!normalizeForSearch(searchText)) {
    return [];
  }
  const index = getDocTextIndexWithTextblockNewlines(doc);
  const foldedRanges = findAllFoldedSubstringRanges(index.accumulatedText, searchText);
  const positions: TextPosition[] = [];
  for (const match of foldedRanges) {
    const resolved = resolveFoldedRangeToDocPosition(doc, index, searchText, match);
    if (resolved) {
      positions.push(resolved);
    }
  }
  return positions;
};

const mapDocPosToTextIndex = (
  index: DocTextIndex,
  docPos: number,
): number => {
  const { positionMapping, accumulatedText } = index;
  if (positionMapping.length === 0) {
    return 0;
  }
  let lo = 0;
  let hi = positionMapping.length - 1;
  let best = positionMapping[0]!;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const mapping = positionMapping[mid]!;
    if (mapping.docPos === docPos) {
      return mapping.textIndex;
    }
    if (mapping.docPos < docPos) {
      best = mapping;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (best.docPos < docPos) {
    const afterBest = best.textIndex + 1;
    if (
      afterBest < accumulatedText.length
      && accumulatedText[afterBest] === '\n'
    ) {
      return afterBest;
    }
    return Math.min(afterBest, accumulatedText.length);
  }
  return best.textIndex;
};

let cachedFoldedRangesByNeedle: {
  accumulatedText: string;
  bySearchText: Map<string, Array<{ start: number; end: number }>>;
} | null = null;

const getFoldedSubstringRangesForIndex = (
  index: DocTextIndex,
  searchText: string,
): Array<{ start: number; end: number }> => {
  if (
    !cachedFoldedRangesByNeedle
    || cachedFoldedRangesByNeedle.accumulatedText !== index.accumulatedText
  ) {
    cachedFoldedRangesByNeedle = {
      accumulatedText: index.accumulatedText,
      bySearchText: new Map(),
    };
  }
  const cached = cachedFoldedRangesByNeedle.bySearchText.get(searchText);
  if (cached) {
    return cached;
  }
  const ranges = findAllFoldedSubstringRanges(index.accumulatedText, searchText);
  cachedFoldedRangesByNeedle.bySearchText.set(searchText, ranges);
  return ranges;
};

const findNearestResolvedTextPositionInDoc = (
  doc: ProseMirrorNode,
  searchText: string,
  targetDocPos: number,
): TextPosition | null => {
  if (!normalizeForSearch(searchText)) {
    return null;
  }
  const index = getDocTextIndexWithTextblockNewlines(doc);
  const foldedRanges = getFoldedSubstringRangesForIndex(index, searchText);
  if (foldedRanges.length === 0) {
    return null;
  }

  const targetTextIndex = mapDocPosToTextIndex(index, targetDocPos);
  let bestIndex = 0;
  let bestDistance = Math.abs(foldedRanges[0]!.start - targetTextIndex);
  for (let i = 1; i < foldedRanges.length; i++) {
    const distance = Math.abs(foldedRanges[i]!.start - targetTextIndex);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }

  const resolvedBest = resolveFoldedRangeToDocPosition(
    doc,
    index,
    searchText,
    foldedRanges[bestIndex]!,
  );
  if (resolvedBest) {
    return resolvedBest;
  }

  const maxExtraCandidates = Math.min(foldedRanges.length, 8);
  for (let offset = 1; offset < maxExtraCandidates; offset++) {
    for (const direction of [-1, 1]) {
      const candidateIndex = bestIndex + direction * offset;
      if (candidateIndex < 0 || candidateIndex >= foldedRanges.length) {
        continue;
      }
      const resolved = resolveFoldedRangeToDocPosition(
        doc,
        index,
        searchText,
        foldedRanges[candidateIndex]!,
      );
      if (resolved) {
        return resolved;
      }
    }
  }
  return null;
};

const mapInsertionTextIndexToDocPos = (
  index: DocTextIndex,
  textIdx: number,
): number | null => {
  if (textIdx < 0) {
    return null;
  }
  if (textIdx < index.accumulatedText.length && index.accumulatedText[textIdx] === '\n') {
    return mapTextIndexToDocPos(
      index.accumulatedText,
      index.positionMapping,
      index.textNodes,
      textIdx,
      true,
    );
  }
  return mapTextIndexToDocPos(
    index.accumulatedText,
    index.positionMapping,
    index.textNodes,
    textIdx,
    false,
  );
};

const mapTextIndexToDocPos = (
  accumulatedText: string,
  positionMapping: TextIndexMapping[],
  textNodes: Array<{ text: string; pos: number }>,
  textIdx: number,
  isEnd: boolean = false,
): number | null => {
  if (textIdx < 0) {
    return null;
  }

  if (isEnd && textIdx > 0) {
    const lastIncluded = positionMapping.find((mapping) => mapping.textIndex === textIdx - 1);
    if (lastIncluded?.atomEndDocPos != null) {
      return lastIncluded.atomEndDocPos;
    }
  }

  if (textIdx >= accumulatedText.length) {
    const lastMapping = positionMapping[positionMapping.length - 1];
    if (lastMapping) {
      if (lastMapping.atomEndDocPos != null) {
        return lastMapping.atomEndDocPos;
      }
      return lastMapping.docPos + (textIdx - lastMapping.textIndex);
    }
    return null;
  }

  if (accumulatedText[textIdx] === '\n') {
    if (isEnd) {
      for (let i = positionMapping.length - 1; i >= 0; i--) {
        if (positionMapping[i].textIndex < textIdx) {
          const mapping = positionMapping[i];
          const textNode = textNodes.find((tn) => mapping.docPos >= tn.pos && mapping.docPos < tn.pos + tn.text.length);
          if (textNode) {
            return textNode.pos + textNode.text.length;
          }
          const nextTextNode = textNodes.find((tn) => tn.pos > mapping.docPos);
          if (nextTextNode) {
            return nextTextNode.pos;
          }
          return mapping.docPos + 1;
        }
      }
    } else {
      for (let i = positionMapping.length - 1; i >= 0; i--) {
        if (positionMapping[i].textIndex < textIdx) {
          const mapping = positionMapping[i];
          const nextTextNode = textNodes.find((tn) => tn.pos > mapping.docPos);
          if (nextTextNode) {
            return nextTextNode.pos;
          }
          return mapping.docPos + 1;
        }
      }
    }
    const firstMapping = positionMapping[0];
    if (firstMapping && firstMapping.textIndex > textIdx) {
      return firstMapping.docPos;
    }
    return null;
  }

  for (let i = 0; i < positionMapping.length; i++) {
    if (positionMapping[i].textIndex >= textIdx) {
      return positionMapping[i].docPos;
    }
  }

  const lastMapping = positionMapping[positionMapping.length - 1];
  if (lastMapping && textIdx >= lastMapping.textIndex) {
    return lastMapping.docPos + (textIdx - lastMapping.textIndex);
  }

  return null;
};

export const findTextPosition = (
  editor: Editor | null,
  searchText: string,
  options?: FindTextPositionOptions
): TextPosition | null => {
  if (!editor) return null;

  const { state } = editor;
  const index = getDocTextIndexWithTextblockNewlines(state.doc);

  if (options?.selectionRange) {
    const { from, to } = options.selectionRange;
    const { accumulatedText: selectedText, positionMapping: selectionMapping } = buildSelectionTextIndex(
      state.doc,
      from,
      to,
    );
    const selectedRanges = findAllFoldedSubstringRanges(selectedText, searchText);

    if (selectedRanges.length > 0) {
      const match = selectedRanges[0];
      const fromDocPos = mapTextIndexToDocPos(selectedText, selectionMapping, index.textNodes, match.start, false);
      const toDocPos = mapTextIndexToDocPos(selectedText, selectionMapping, index.textNodes, match.end, true);
      if (fromDocPos !== null && toDocPos !== null) {
        const actualText = getDocumentPlainText(state.doc, fromDocPos, toDocPos);
        if (textsEqualForSearch(actualText, searchText)) {
          return { from: fromDocPos, to: toDocPos };
        }
      }
    }

    return null;
  }

  const foldedRanges = findAllFoldedSubstringRanges(index.accumulatedText, searchText);
  for (const match of foldedRanges) {
    const resolved = resolveFoldedRangeToDocPosition(state.doc, index, searchText, match);
    if (!resolved) {
      continue;
    }
    if (options?.minDocPos !== undefined && resolved.from <= options.minDocPos) {
      continue;
    }
    return resolved;
  }

  return null;
};

export const findAllTextPositions = (
  editor: Editor | null,
  searchText: string
): TextPosition[] => {
  if (!editor) return [];

  const { state } = editor;
  if (!normalizeForSearch(searchText)) return [];

  const textNodes: Array<{ text: string; pos: number }> = [];
  const positionMapping: TextIndexMapping[] = [];
  let textIndex = 0;
  let accumulatedText = '';

  state.doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i++) {
        positionMapping.push({ textIndex, docPos: pos + i });
        textIndex += 1;
      }
      accumulatedText += node.text;
      textNodes.push({ text: node.text, pos });
      return;
    }

    if (isMentionNode(node)) {
      const mentionText = getLeafNodePlainText(node);
      const atomEndDocPos = pos + node.nodeSize;
      for (let i = 0; i < mentionText.length; i++) {
        positionMapping.push({ textIndex, docPos: pos, atomEndDocPos });
        textIndex += 1;
      }
      accumulatedText += mentionText;
    }
  });

  const ranges = findAllFoldedSubstringRanges(accumulatedText, searchText);
  const positions: TextPosition[] = [];

  for (const range of ranges) {
    const from = mapTextIndexToDocPos(
      accumulatedText,
      positionMapping,
      textNodes,
      range.start,
      false,
    );
    let to = mapTextIndexToDocPos(
      accumulatedText,
      positionMapping,
      textNodes,
      range.end,
      true,
    );
    if (from === null || to === null) {
      continue;
    }
    if (to <= from) {
      to = from + 1;
    }
    positions.push({
      from,
      to,
      plainFrom: range.start,
      plainTo: range.end,
    });
  }

  return positions;
};

export const findTextPositionAtOccurrence = (
  editor: Editor | null,
  searchText: string,
  occurrenceIndex: number = 0,
  options?: FindTextPositionOptions,
): TextPosition | null => {
  if (!editor || occurrenceIndex < 0) return null;

  if (options?.selectionRange) {
    let minDocPos: number | undefined;
    for (let i = 0; i <= occurrenceIndex; i++) {
      const position = findTextPosition(editor, searchText, { ...options, minDocPos });
      if (!position) return null;
      if (i === occurrenceIndex) return position;
      minDocPos = position.to - 1;
    }
    return null;
  }

  if (!normalizeForSearch(searchText)) {
    return null;
  }
  const doc = editor.state.doc;
  const index = getDocTextIndexWithTextblockNewlines(doc);
  const foldedRanges = getFoldedSubstringRangesForIndex(index, searchText);
  let resolvedCount = 0;
  for (const match of foldedRanges) {
    const resolved = resolveFoldedRangeToDocPosition(doc, index, searchText, match);
    if (!resolved) {
      continue;
    }
    if (resolvedCount === occurrenceIndex) {
      return resolved;
    }
    resolvedCount += 1;
  }
  return null;
};

export type SuggestionScopeFields = {
  scopeFromPos?: number | null;
  scopeToPos?: number | null;
  scopeSelectedText?: string | null;
};

export type SuggestionPositionFields = {
  fromPos?: number | null;
  toPos?: number | null;
  fromRel?: Record<string, unknown> | null;
  toRel?: Record<string, unknown> | null;
  contextBefore?: string | null;
  contextAfter?: string | null;
};

export const INSERTION_CONTEXT_MAX_LENGTH = 64;

export type InsertionContext = {
  contextBefore: string;
  contextAfter: string;
};

const trimInsertionContextBefore = (slice: string, reachedStart: boolean): string => {
  if (reachedStart || !slice) {
    return slice;
  }
  const whitespaceIndex = slice.indexOf(' ');
  if (whitespaceIndex === -1 || whitespaceIndex >= slice.length - 1) {
    return slice;
  }
  return slice.slice(whitespaceIndex + 1);
};

const trimInsertionContextAfter = (slice: string, reachedEnd: boolean): string => {
  if (reachedEnd || !slice) {
    return slice;
  }
  const whitespaceIndex = slice.lastIndexOf(' ');
  if (whitespaceIndex <= 0) {
    return slice;
  }
  return slice.slice(0, whitespaceIndex);
};

export const captureInsertionContext = (
  editor: Editor | null,
  insertPos: number,
  maxLength: number = INSERTION_CONTEXT_MAX_LENGTH,
): InsertionContext | null => {
  if (!editor || editor.isDestroyed || maxLength <= 0) {
    return null;
  }

  const doc = editor.state.doc;
  const docSize = doc.content.size;
  const safePos = Math.max(0, Math.min(insertPos, docSize));
  const index = getDocTextIndexWithTextblockNewlines(doc);
  if (!index.accumulatedText) {
    return { contextBefore: '', contextAfter: '' };
  }

  const textIndex = mapDocPosToTextIndex(index, safePos);
  const beforeStart = Math.max(0, textIndex - maxLength);
  const afterEnd = Math.min(index.accumulatedText.length, textIndex + maxLength);
  const contextBefore = trimInsertionContextBefore(
    index.accumulatedText.slice(beforeStart, textIndex),
    beforeStart === 0,
  );
  const contextAfter = trimInsertionContextAfter(
    index.accumulatedText.slice(textIndex, afterEnd),
    afterEnd >= index.accumulatedText.length,
  );

  return { contextBefore, contextAfter };
};

const insertionContextMatchesAtTextIndex = (
  accumulatedText: string,
  textIndex: number,
  contextBefore: string,
  contextAfter: string,
): boolean => {
  if (contextBefore) {
    if (textIndex < contextBefore.length) {
      return false;
    }
    const beforeSlice = accumulatedText.slice(textIndex - contextBefore.length, textIndex);
    if (!textsEqualForSearch(beforeSlice, contextBefore)) {
      return false;
    }
  }
  if (contextAfter) {
    if (textIndex + contextAfter.length > accumulatedText.length) {
      return false;
    }
    const afterSlice = accumulatedText.slice(textIndex, textIndex + contextAfter.length);
    if (!textsEqualForSearch(afterSlice, contextAfter)) {
      return false;
    }
  }
  return Boolean(contextBefore || contextAfter);
};

const findNearestInsertionTextIndex = (
  index: DocTextIndex,
  targetDocPos: number,
  contextBefore: string,
  contextAfter: string,
): number | null => {
  const { accumulatedText } = index;
  if (!contextBefore && !contextAfter) {
    return null;
  }

  const targetTextIndex = mapDocPosToTextIndex(index, targetDocPos);
  type InsertionCandidate = { textIndex: number; afterMatches: boolean };
  const candidates: InsertionCandidate[] = [];

  if (contextBefore) {
    const beforeRanges = findAllFoldedSubstringRanges(accumulatedText, contextBefore);
    for (const range of beforeRanges) {
      const candidate = range.end;
      const afterMatches = !contextAfter
        || insertionContextMatchesAtTextIndex(accumulatedText, candidate, '', contextAfter);
      candidates.push({ textIndex: candidate, afterMatches });
    }
  } else if (contextAfter) {
    const afterRanges = findAllFoldedSubstringRanges(accumulatedText, contextAfter);
    for (const range of afterRanges) {
      candidates.push({ textIndex: range.start, afterMatches: true });
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((a, b) => {
    if (a.afterMatches !== b.afterMatches) {
      return a.afterMatches ? -1 : 1;
    }
    return Math.abs(a.textIndex - targetTextIndex) - Math.abs(b.textIndex - targetTextIndex);
  });

  return candidates[0]!.textIndex;
};

export type ScrollToSuggestionTarget = {
  id: string;
  originalText: string;
  suggestionText?: string;
  issueType?: string;
} & SuggestionScopeFields & SuggestionPositionFields;

export type SuggestionOrderingFields = ScrollToSuggestionTarget & {
  status: string;
  createdAt: string;
};

export const isDeletionSuggestion = (
  suggestion: {
    issueType?: string | null;
    suggestionText?: string | null;
  },
): boolean => {
  if (suggestion.issueType === 'strength') {
    return false;
  }
  return (suggestion.suggestionText ?? '') === '';
};

export const isInsertionSuggestion = (
  suggestion: {
    originalText?: string | null;
    suggestionText?: string | null;
    fromPos?: number | null;
    toPos?: number | null;
  },
): boolean => {
  if ((suggestion.originalText ?? '') !== '') {
    return false;
  }
  if (suggestion.suggestionText != null) {
    return suggestion.suggestionText !== '';
  }
  return suggestion.fromPos != null
    && suggestion.toPos != null
    && suggestion.fromPos === suggestion.toPos;
};

export const sortPendingSuggestions = <T extends SuggestionOrderingFields>(
  suggestions: T[],
): T[] => {
  return suggestions.filter((suggestion) => suggestion.status === 'pending');
};

export const sortPendingSuggestionsByDocumentOrder = <T extends SuggestionOrderingFields>(
  editor: Editor | null,
  suggestions: T[],
): T[] => {
  const pending = sortPendingSuggestions(suggestions);
  if (pending.length <= 1) {
    return pending;
  }

  if (!editor) {
    return [...pending].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  const creationOrder = [...pending].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const positioned = pending.map((suggestion) => ({
    suggestion,
    from: findSuggestionTextPosition(editor, suggestion, creationOrder)?.from ?? Number.MAX_SAFE_INTEGER,
    createdAt: new Date(suggestion.createdAt).getTime(),
  }));

  positioned.sort((a, b) => {
    if (a.from !== b.from) {
      return a.from - b.from;
    }
    return a.createdAt - b.createdAt;
  });

  return positioned.map((item) => item.suggestion);
};

export type CommentOrderingFields = {
  id: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
};

export const sortCommentsByDocumentOrder = <T extends CommentOrderingFields>(
  editor: Editor | null,
  comments: T[],
): T[] => {
  if (comments.length <= 1) {
    return comments;
  }

  if (!editor) {
    return [...comments].sort((a, b) => {
      if (a.fromPos !== b.fromPos) {
        return a.fromPos - b.fromPos;
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }

  const positioned = comments.map((comment) => {
    const position = findCommentTextPosition(editor, comment);
    return {
      comment,
      from: position?.from ?? Number.MAX_SAFE_INTEGER,
      createdAt: new Date(comment.createdAt).getTime(),
    };
  });

  positioned.sort((a, b) => {
    if (a.from !== b.from) {
      return a.from - b.from;
    }
    return a.createdAt - b.createdAt;
  });

  return positioned.map((item) => item.comment);
};

export type ResolvedCommentOrderingFields = {
  id: string;
  deletedAt?: string | null;
};

export const sortCommentsByResolutionOrder = <T extends ResolvedCommentOrderingFields>(
  comments: T[],
): T[] => {
  if (comments.length <= 1) {
    return comments;
  }

  return [...comments].sort((a, b) => {
    const aResolvedAt = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
    const bResolvedAt = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;
    return bResolvedAt - aResolvedAt;
  });
};

/**
 * Returns which document occurrence (0-based) a suggestion should target among
 * pending suggestions that share the same `originalText`.
 *
 * Only disambiguates when multiple suggestions in `suggestionsInOrder` have
 * identical `originalText` (case-insensitive). A single suggestion always
 * receives index 0, even when the text appears multiple times in the document.
 */
export const getSuggestionOccurrenceIndex = (
  suggestionId: string,
  originalText: string,
  suggestionsInOrder: ScrollToSuggestionTarget[],
): number => {
  const normalized = originalText.toLowerCase();
  let occurrenceIndex = 0;

  for (const suggestion of suggestionsInOrder) {
    if (suggestion.id === suggestionId) {
      return occurrenceIndex;
    }
    if (suggestion.originalText.toLowerCase() === normalized) {
      occurrenceIndex += 1;
    }
  }

  return 0;
};

const forEachInlineHighlightMark = (
  doc: ProseMirrorNode,
  visit: (node: ProseMirrorNode, pos: number, mark: Mark) => void,
): void => {
  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (!node.isInline || !node.marks?.length) {
      return;
    }
    node.marks.forEach((mark: Mark) => {
      if (mark.type.name === 'highlightColor') {
        visit(node, pos, mark);
      }
    });
  });
};

export const findSuggestionMarkPosition = (
  editor: Editor | null,
  suggestionId: string,
): TextPosition | null => {
  if (!editor) return null;

  let range: TextPosition | null = null;

  forEachInlineHighlightMark(editor.state.doc, (node, pos, mark) => {
    if (mark.attrs.suggestionId !== suggestionId) {
      return;
    }
    const from = pos;
    const to = pos + node.nodeSize;
    if (!range) {
      range = { from, to };
    } else {
      range = {
        from: Math.min(range.from, from),
        to: Math.max(range.to, to),
      };
    }
  });

  return range;
};

export const recalculateSuggestionScopeRange = (
  editor: Editor | null,
  scope: SuggestionScopeFields,
): TextPosition | null => {
  if (!editor || scope.scopeFromPos == null || scope.scopeToPos == null) {
    return null;
  }

  const docSize = editor.state.doc.content.size;
  let from = scope.scopeFromPos;
  let to = scope.scopeToPos;

  if (scope.scopeSelectedText?.trim()) {
    const recalculated = recalculateCommentPosition(editor, {
      id: '',
      fromPos: scope.scopeFromPos,
      toPos: scope.scopeToPos,
      selectedText: scope.scopeSelectedText,
    });
    from = recalculated.fromPos;
    to = recalculated.toPos;
  }

  if (from < 0 || to > docSize || from >= to) {
    return null;
  }

  return { from, to };
};

export const recalculateSuggestionPosition = (
  editor: Editor | null,
  suggestion: SuggestionPositionFields & { originalText: string },
  ydoc?: Y.Doc | null,
): TextPosition | null => {
  if (!editor || suggestion.fromPos == null || suggestion.toPos == null) {
    return null;
  }

  const relativeRange = resolveDocAnchor(editor, ydoc, {
    fromRel: suggestion.fromRel ?? null,
    toRel: suggestion.toRel ?? null,
  });
  if (relativeRange) {
    if (!suggestion.originalText && relativeRange.from === relativeRange.to) {
      return relativeRange;
    }
    if (
      suggestion.originalText
      && relativeRange.from < relativeRange.to
      && textsEqualForSearch(
        getDocumentPlainText(editor.state.doc, relativeRange.from, relativeRange.to),
        stripInlineMarkdown(suggestion.originalText),
      )
    ) {
      return relativeRange;
    }
  }

  if (!suggestion.originalText && suggestion.fromPos === suggestion.toPos) {
    const doc = editor.state.doc;
    const docSize = doc.content.size;
    const contextBefore = suggestion.contextBefore ?? '';
    const contextAfter = suggestion.contextAfter ?? '';
    const hasContext = Boolean(contextBefore || contextAfter);
    const storedInRange = suggestion.fromPos >= 0 && suggestion.fromPos <= docSize;

    if (hasContext) {
      const index = getDocTextIndexWithTextblockNewlines(doc);
      const storedTextIndex = storedInRange
        ? mapDocPosToTextIndex(index, suggestion.fromPos)
        : null;
      if (
        storedTextIndex != null
        && (
          contextBefore
            ? insertionContextMatchesAtTextIndex(
              index.accumulatedText,
              storedTextIndex,
              contextBefore,
              '',
            )
            : insertionContextMatchesAtTextIndex(
              index.accumulatedText,
              storedTextIndex,
              '',
              contextAfter,
            )
        )
      ) {
        return { from: suggestion.fromPos, to: suggestion.toPos };
      }

      const nearestTextIndex = findNearestInsertionTextIndex(
        index,
        suggestion.fromPos,
        contextBefore,
        contextAfter,
      );
      if (nearestTextIndex != null) {
        const mappedPos = mapInsertionTextIndexToDocPos(index, nearestTextIndex);
        if (mappedPos != null && mappedPos >= 0 && mappedPos <= docSize) {
          return { from: mappedPos, to: mappedPos };
        }
      }
    }

    if (!storedInRange) {
      return null;
    }
    return { from: suggestion.fromPos, to: suggestion.toPos };
  }

  const searchText = stripInlineMarkdown(suggestion.originalText);
  if (!normalizeForSearch(searchText)) {
    return null;
  }

  const doc = editor.state.doc;
  const docSize = doc.content.size;
  if (
    suggestion.fromPos >= 0
    && suggestion.toPos <= docSize
    && suggestion.fromPos < suggestion.toPos
    && textsEqualForSearch(
      getDocumentPlainText(doc, suggestion.fromPos, suggestion.toPos),
      searchText,
    )
  ) {
    return { from: suggestion.fromPos, to: suggestion.toPos };
  }

  const nearest = findNearestResolvedTextPositionInDoc(
    doc,
    searchText,
    suggestion.fromPos,
  );
  if (!nearest) {
    return null;
  }

  if (!textsEqualForSearch(
    getDocumentPlainText(doc, nearest.from, nearest.to),
    searchText,
  )) {
    return null;
  }

  return nearest;
};

const getSuggestionSearchOptions = (
  editor: Editor | null,
  suggestion: SuggestionScopeFields,
): FindTextPositionOptions | undefined => {
  const scopeRange = recalculateSuggestionScopeRange(editor, suggestion);
  if (!scopeRange) {
    return undefined;
  }

  return { selectionRange: scopeRange };
};

const suggestionMarkMatchesOriginalText = (
  editor: Editor,
  markPosition: TextPosition,
  originalText: string,
): boolean => {
  if (!originalText) {
    return markPosition.from === markPosition.to;
  }
  return textsEqualForSearch(
    getDocumentPlainText(editor.state.doc, markPosition.from, markPosition.to),
    stripInlineMarkdown(originalText),
  );
};

/**
 * Resolves a suggestion's document range using, in order:
 * 1. Stored span positions recalculated against `originalText` (authoritative; beats stale marks).
 * 2. An existing highlight mark tagged with `suggestionId` whose text still matches `originalText`.
 * 3. The Nth substring occurrence within the selection scope when scope bounds exist.
 * 4. The Nth substring occurrence in the full document.
 * 5. Scoped or full-document substring search as fallback.
 */
export const findSuggestionTextPosition = (
  editor: Editor | null,
  suggestion: ScrollToSuggestionTarget,
  suggestionsInOrder: ScrollToSuggestionTarget[],
  ydoc?: Y.Doc | null,
): TextPosition | null => {
  const storedPosition = recalculateSuggestionPosition(editor, suggestion, ydoc);
  if (storedPosition) return storedPosition;

  const plainOriginalText = stripInlineMarkdown(suggestion.originalText);
  const markPosition = findSuggestionMarkPosition(editor, suggestion.id);
  if (
    markPosition
    && editor
    && suggestionMarkMatchesOriginalText(editor, markPosition, plainOriginalText)
  ) {
    return markPosition;
  }

  const searchOptions = getSuggestionSearchOptions(editor, suggestion);
  const hasScopedSearch = searchOptions?.selectionRange != null;

  const occurrenceIndex = getSuggestionOccurrenceIndex(
    suggestion.id,
    plainOriginalText,
    suggestionsInOrder.map((candidate) => ({
      ...candidate,
      originalText: stripInlineMarkdown(candidate.originalText),
    })),
  );

  const occurrencePosition = findTextPositionAtOccurrence(
    editor,
    plainOriginalText,
    occurrenceIndex,
    searchOptions,
  );
  if (occurrencePosition) return occurrencePosition;

  if (hasScopedSearch) {
    return findTextPosition(editor, plainOriginalText, searchOptions);
  }

  return findTextPosition(editor, plainOriginalText);
};

export const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
  let scrollContainer = element;
  while (scrollContainer && scrollContainer !== document.body) {
    const style = window.getComputedStyle(scrollContainer);
    if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll' || style.overflowY === 'scroll') {
      return scrollContainer;
    }
    scrollContainer = scrollContainer.parentElement;
  }
  return null;
};

export const preserveScrollDuring = (editor: Editor, action: () => void): void => {
  const editorDom = getMountedEditorDom(editor);
  if (!editorDom) {
    action();
    return;
  }
  const scrollContainer = findScrollContainer(editorDom);
  if (!scrollContainer) {
    action();
    return;
  }
  const scrollTop = scrollContainer.scrollTop;
  const scrollLeft = scrollContainer.scrollLeft;
  action();
  const restore = (): void => {
    scrollContainer.scrollTop = scrollTop;
    scrollContainer.scrollLeft = scrollLeft;
  };
  restore();
  requestAnimationFrame(() => {
    restore();
    requestAnimationFrame(restore);
  });
};

export const restoreEditorTextSelection = (
  editor: Editor,
  range: { from: number; to: number },
): void => {
  if (editor.isDestroyed) {
    return;
  }
  preserveScrollDuring(editor, () => {
    editor
      .chain()
      .setTextSelection({ from: range.from, to: range.to })
      .focus(undefined, { scrollIntoView: false })
      .run();
  });
};

export const isPointerBelowEditorContent = (
  editor: Editor,
  clientY: number,
): boolean => {
  const editorDom = getMountedEditorDom(editor);
  if (!editorDom) {
    return false;
  }
  const lastChild = editorDom.lastElementChild;
  if (!(lastChild instanceof HTMLElement)) {
    return false;
  }
  return clientY > lastChild.getBoundingClientRect().bottom;
};

export const focusEditorAtEndWithoutScrolling = (editor: Editor): void => {
  if (editor.isDestroyed) {
    return;
  }
  editor.chain().focus('end', { scrollIntoView: false }).run();
};

const CLICK_BELOW_CONTENT_IGNORE_CLOSEST =
  'button, a, input, textarea, select, [role="dialog"], [role="menu"], [data-radix-popper-content-wrapper], .paragraph-section-menu-floating, .paragraph-section-menu-floating-shell';

export const handleMouseDownBelowEditorContent = (
  editor: Editor | null,
  event: Pick<MouseEvent, 'clientY' | 'target' | 'preventDefault'>,
  options?: { editable?: boolean },
): boolean => {
  if (!editor || editor.isDestroyed) {
    return false;
  }
  if (options?.editable === false) {
    return false;
  }

  const target = event.target;
  if (target instanceof Element && target.closest(CLICK_BELOW_CONTENT_IGNORE_CLOSEST)) {
    return false;
  }

  if (!isPointerBelowEditorContent(editor, event.clientY)) {
    return false;
  }

  event.preventDefault();
  focusEditorAtEndWithoutScrolling(editor);
  return true;
};

export const SUGGESTION_PULSE_CLASS = 'suggestion-pulse';
export const COMMENT_WHITESPACE_ANCHOR_CLASS = 'comment-whitespace-anchor';
export const COMMENT_LOCATION_INDICATOR_CLASS = 'comment-location-indicator';

export const findSuggestionInDocument = (
  editor: Editor | null,
  suggestion: ScrollToSuggestionTarget,
  suggestionsInOrder: ScrollToSuggestionTarget[] = [],
): boolean => {
  if (!editor) return false;

  const editorRoot = editor.view.dom as HTMLElement;
  if (editorRoot.querySelector(`[data-suggestion-id="${CSS.escape(suggestion.id)}"]`)) {
    return true;
  }

  return findSuggestionTextPosition(editor, suggestion, suggestionsInOrder) !== null;
};

const DEFAULT_SUGGESTION_HIGHLIGHT_COLOR = 'rgba(234, 179, 8, 0.3)';
const USER_EDIT_SUGGESTION_HIGHLIGHT_COLOR = 'rgba(79, 159, 212, 0.35)';
const COMMENT_HIGHLIGHT_COLOR = 'rgba(59, 130, 246, 0.3)';

const getSuggestionHighlightColor = (issueType?: string): string => {
  if (issueType === 'user_edit') {
    return USER_EDIT_SUGGESTION_HIGHLIGHT_COLOR;
  }
  return DEFAULT_SUGGESTION_HIGHLIGHT_COLOR;
};

export const findMarkElementAtPosition = (
  editor: Editor,
  position: TextPosition,
): HTMLElement | null => {
  const { view } = editor;
  const domAtPos = view.domAtPos(position.from);
  let node: Node | null = domAtPos.node;

  if (node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }

  while (node && node !== view.dom) {
    if (node instanceof HTMLElement && node.tagName === 'MARK' && node.hasAttribute('data-suggestion-id')) {
      return node;
    }
    node = node.parentElement;
  }

  return null;
};

export const ensureSuggestionMarkInDocument = (
  editor: Editor,
  suggestion: ScrollToSuggestionTarget,
  suggestionsInOrder: ScrollToSuggestionTarget[],
): HTMLElement | null => {
  if (editor.isDestroyed) return null;

  const editorRoot = editor.view.dom as HTMLElement;

  if (isInsertionSuggestion(suggestion)) {
    const markerSuggestions = suggestionsInOrder.some((item) => item.id === suggestion.id)
      ? suggestionsInOrder
      : [...suggestionsInOrder, suggestion];
    syncSuggestionInsertMarkers(editor, markerSuggestions);
    return editorRoot.querySelector(
      `[data-suggestion-id="${CSS.escape(suggestion.id)}"]`,
    ) as HTMLElement | null;
  }

  const position = findSuggestionTextPosition(editor, suggestion, suggestionsInOrder);
  if (!position || position.from >= position.to) {
    return null;
  }

  const markType = editor.state.schema.marks.highlightColor;
  if (!markType) {
    return null;
  }

  const existingMarkPosition = findSuggestionMarkPosition(editor, suggestion.id);
  const markAlreadyCorrect = existingMarkPosition
    && existingMarkPosition.from === position.from
    && existingMarkPosition.to === position.to;

  if (!markAlreadyCorrect) {
    let tr = editor.state.tr;
    if (existingMarkPosition) {
      tr = tr.removeMark(existingMarkPosition.from, existingMarkPosition.to, markType);
    }
    tr = tr.addMark(
      position.from,
      position.to,
      markType.create({
        color: getSuggestionHighlightColor(suggestion.issueType),
        suggestionId: suggestion.id,
      }),
    );
    editor.view.dispatch(tr);
  }

  const markAtPosition = findMarkElementAtPosition(editor, position);
  if (markAtPosition) {
    return markAtPosition;
  }

  return editorRoot.querySelector(
    `[data-suggestion-id="${CSS.escape(suggestion.id)}"]`,
  ) as HTMLElement | null;
};

export const syncSuggestionInsertMarkers = (
  editor: Editor | null,
  suggestions: Array<ScrollToSuggestionTarget & { status?: string }>,
): void => {
  if (!editor || editor.isDestroyed) {
    return;
  }
  if (typeof editor.commands.setSuggestionInsertMarkers !== 'function') {
    return;
  }

  const pendingSuggestions = suggestions.filter(
    (suggestion) => !suggestion.status || suggestion.status === 'pending',
  );
  const markers = pendingSuggestions
    .filter((suggestion) => isInsertionSuggestion(suggestion))
    .flatMap((suggestion) => {
      const position = findSuggestionTextPosition(editor, suggestion, pendingSuggestions);
      if (!position) {
        return [];
      }
      return [{
        id: suggestion.id,
        pos: position.from,
        issueType: suggestion.issueType,
      }];
    });

  editor.commands.setSuggestionInsertMarkers(markers);
};

const scrollElementToContainerCenter = (
  element: HTMLElement,
  scrollContainer: HTMLElement | null,
): void => {
  if (scrollContainer) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const scrollTop = scrollContainer.scrollTop
      + (elementRect.top - containerRect.top)
      - (containerRect.height / 2)
      + (elementRect.height / 2);

    scrollContainer.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });
    return;
  }

  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const isWhitespaceOnlyMarkElement = (markElement: HTMLElement): boolean => {
  const text = markElement.textContent ?? '';
  return text.length > 0 && text.trim().length === 0;
};

const isMarkElementVisuallyHighlightable = (markElement: HTMLElement): boolean => {
  const rect = markElement.getBoundingClientRect();
  return rect.width >= 3 && rect.height >= 3;
};

const pulseMarkElement = (markElement: HTMLElement): void => {
  markElement.classList.remove(SUGGESTION_PULSE_CLASS);
  void markElement.offsetWidth;
  if (isWhitespaceOnlyMarkElement(markElement)) {
    markElement.classList.add(COMMENT_WHITESPACE_ANCHOR_CLASS);
  }
  markElement.classList.add(SUGGESTION_PULSE_CLASS);

  const removePulse = (): void => {
    markElement.classList.remove(SUGGESTION_PULSE_CLASS);
    markElement.classList.remove(COMMENT_WHITESPACE_ANCHOR_CLASS);
    markElement.removeEventListener('animationend', removePulse);
  };

  markElement.addEventListener('animationend', removePulse);
};

const clearMarkPulses = (editorRoot: HTMLElement): void => {
  editorRoot.querySelectorAll(`.${SUGGESTION_PULSE_CLASS}`).forEach((element) => {
    element.classList.remove(SUGGESTION_PULSE_CLASS);
    element.classList.remove(COMMENT_WHITESPACE_ANCHOR_CLASS);
  });
};

const restoreCommentIndicatorHostPosition = (host: HTMLElement | null): void => {
  if (!host || host.dataset.commentIndicatorHost === undefined) {
    return;
  }
  host.style.position = host.dataset.commentIndicatorHost;
  delete host.dataset.commentIndicatorHost;
};

const clearCommentLocationIndicators = (): void => {
  document.querySelectorAll(`.${COMMENT_LOCATION_INDICATOR_CLASS}`).forEach((element) => {
    const host = element.parentElement;
    element.remove();
    restoreCommentIndicatorHostPosition(host);
  });
};

const resolveCommentLocationIndicatorHost = (
  editorRoot: HTMLElement,
  scrollContainer: HTMLElement | null,
): HTMLElement => {
  const candidates = [
    scrollContainer,
    findScrollContainer(editorRoot.parentElement),
    editorRoot.parentElement,
  ];
  for (const candidate of candidates) {
    if (candidate && candidate !== editorRoot && candidate.isConnected) {
      return candidate;
    }
  }
  return document.body;
};

const showTemporaryCommentLocationIndicator = (
  editor: Editor,
  pos: number,
  scrollContainer: HTMLElement | null,
): HTMLElement => {
  clearCommentLocationIndicators();

  const maxPos = editor.state.doc.content.size;
  const safePos = Math.max(0, Math.min(pos, maxPos));

  let coords: { left: number; top: number; bottom: number } | null = null;
  try {
    coords = editor.view.coordsAtPos(safePos);
  } catch {
    coords = null;
  }

  const editorRoot = editor.view.dom as HTMLElement;
  const host = resolveCommentLocationIndicatorHost(editorRoot, scrollContainer);
  const useFixedFallback = host === document.body;

  const indicator = document.createElement('span');
  indicator.className = `${COMMENT_LOCATION_INDICATOR_CLASS} ${SUGGESTION_PULSE_CLASS}`;
  indicator.setAttribute('aria-hidden', 'true');
  indicator.style.position = useFixedFallback ? 'fixed' : 'absolute';
  indicator.style.width = '4px';
  indicator.style.borderRadius = '2px';
  indicator.style.backgroundColor = 'rgba(59, 130, 246, 0.9)';
  indicator.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.45)';
  indicator.style.pointerEvents = 'none';
  indicator.style.zIndex = useFixedFallback ? '10050' : '20';

  if (coords) {
    if (useFixedFallback) {
      indicator.style.left = `${Math.max(0, coords.left - 2)}px`;
      indicator.style.top = `${coords.top}px`;
    } else {
      const hostRect = host.getBoundingClientRect();
      indicator.style.left = `${coords.left - hostRect.left + host.scrollLeft - 2}px`;
      indicator.style.top = `${coords.top - hostRect.top + host.scrollTop}px`;
    }
    indicator.style.height = `${Math.max(18, coords.bottom - coords.top)}px`;
  } else {
    indicator.style.left = '0px';
    indicator.style.top = '0px';
    indicator.style.height = '18px';
  }

  if (!useFixedFallback) {
    const hostPosition = window.getComputedStyle(host).position;
    if (hostPosition === 'static') {
      host.dataset.commentIndicatorHost = host.style.position;
      host.style.position = 'relative';
    }
  }

  host.appendChild(indicator);

  const remove = (): void => {
    if (!indicator.isConnected) {
      return;
    }
    const indicatorHost = indicator.parentElement;
    indicator.remove();
    restoreCommentIndicatorHostPosition(indicatorHost);
    indicator.removeEventListener('animationend', remove);
  };
  indicator.addEventListener('animationend', remove);
  window.setTimeout(remove, 3200);

  return indicator;
};

export const scrollToSuggestionMark = (
  editor: Editor | null,
  suggestion: ScrollToSuggestionTarget,
  scrollContainerRef?: React.RefObject<HTMLElement>,
  suggestionsInOrder: ScrollToSuggestionTarget[] = [],
): boolean => {
  if (!editor || editor.isDestroyed) return false;

  if (!findSuggestionInDocument(editor, suggestion, suggestionsInOrder)) {
    return false;
  }

  const { view } = editor;
  const editorRoot = view.dom as HTMLElement;
  const scrollContainer = scrollContainerRef?.current || findScrollContainer(editorRoot.parentElement);

  clearMarkPulses(editorRoot);

  const revealSuggestion = (): void => {
    if (editor.isDestroyed) return;

    if (isInsertionSuggestion(suggestion)) {
      syncSuggestionInsertMarkers(
        editor,
        suggestionsInOrder.some((item) => item.id === suggestion.id)
          ? suggestionsInOrder
          : [...suggestionsInOrder, suggestion],
      );
    }

    const markElement = ensureSuggestionMarkInDocument(editor, suggestion, suggestionsInOrder);
    if (markElement) {
      scrollElementToContainerCenter(markElement, scrollContainer);
      pulseMarkElement(markElement);
      return;
    }

    const position = findSuggestionTextPosition(editor, suggestion, suggestionsInOrder);
    if (!position) return;

    const coords = view.coordsAtPos(position.from);
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop + (coords.top - containerRect.top) - (containerRect.height / 2);
      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    } else {
      view.dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  window.requestAnimationFrame(revealSuggestion);
  return true;
};

export const selectFirstNWordsExpandingToSentence = (
  editor: Editor | null,
  targetWordCount: number,
  startFrom: number = 0
): { from: number; to: number; text: string } | null => {
  if (!editor) return null;

  const { state } = editor;
  const fullText = getDocumentPlainText(state.doc, startFrom, state.doc.content.size);
  const words = fullText.trim().split(/\s+/).filter((word: string) => word.length > 0);

  if (words.length === 0) return null;

  const actualWordCount = Math.min(targetWordCount, words.length);
  let wordIndex = 0;
  let charCount = 0;
  let fromPos = startFrom;
  let toPos = startFrom;

  const textNodes: Array<{ text: string; pos: number }> = [];
  state.doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.isText && node.text && pos >= startFrom) {
      textNodes.push({ text: node.text, pos });
    }
  });

  for (const node of textNodes) {
    const nodeText = node.text;
    const nodeWords = nodeText.split(/\s+/).filter((w: string) => w.length > 0);
    
    for (let i = 0; i < nodeWords.length; i++) {
      const word = nodeWords[i];
      const wordStartInText = nodeText.indexOf(word, i > 0 ? nodeText.indexOf(nodeWords[i - 1]) + nodeWords[i - 1].length : 0);
      
      if (wordIndex === 0) {
        fromPos = node.pos + wordStartInText;
      }
      
      wordIndex++;
      
      if (wordIndex === actualWordCount) {
        toPos = node.pos + wordStartInText + word.length;
        break;
      }
    }
    
    if (wordIndex >= actualWordCount) break;
  }

  if (fromPos === startFrom && toPos === startFrom) {
    return null;
  }

  const textAfter750Words = getDocumentPlainText(
    state.doc,
    toPos,
    Math.min(toPos + 500, state.doc.content.size),
  );
  const sentenceEndMatch = textAfter750Words.match(/[.!?]\s*/);
  
  if (sentenceEndMatch && sentenceEndMatch.index !== undefined) {
    const sentenceEndOffset = sentenceEndMatch.index + sentenceEndMatch[0].length;
    let currentPos = toPos;
    let remainingChars = sentenceEndOffset;
    
    for (const node of textNodes) {
      const nodeStart = node.pos;
      const nodeEnd = node.pos + node.text.length;
      
      if (currentPos >= nodeStart && currentPos < nodeEnd) {
        const offsetInNode = currentPos - nodeStart;
        const remainingInNode = node.text.length - offsetInNode;
        
        if (remainingChars <= remainingInNode) {
          toPos = nodeStart + offsetInNode + remainingChars;
          break;
        } else {
          remainingChars -= remainingInNode;
          currentPos = nodeEnd;
        }
      } else if (currentPos < nodeStart) {
        if (remainingChars <= node.text.length) {
          toPos = nodeStart + remainingChars;
          break;
        } else {
          remainingChars -= node.text.length;
          currentPos = nodeEnd;
        }
      }
    }
  }

  const finalText = getDocumentMarkdownText(state.doc, fromPos, toPos);
  return { from: fromPos, to: toPos, text: finalText };
};

type CommentWithSelectedText = {
  id: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  fromRel?: Record<string, unknown> | null;
  toRel?: Record<string, unknown> | null;
};

export const findCommentMarkPosition = (
  editor: Editor | null,
  commentId: string,
): TextPosition | null => {
  if (!editor) return null;

  let range: TextPosition | null = null;

  forEachInlineHighlightMark(editor.state.doc, (node, pos, mark) => {
    if (String(mark.attrs.commentId) !== String(commentId)) {
      return;
    }
    const from = pos;
    const to = pos + node.nodeSize;
    if (!range) {
      range = { from, to };
    } else {
      range = {
        from: Math.min(range.from, from),
        to: Math.max(range.to, to),
      };
    }
  });

  return range;
};

const getCommentSearchText = (selectedText: string | undefined): string => {
  if (selectedText == null) {
    return '';
  }
  const trimmed = selectedText.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  return selectedText;
};

const commentSelectedTextMatchesRange = (
  editor: Editor,
  range: TextPosition,
  selectedText: string | undefined,
): boolean => {
  const searchText = getCommentSearchText(selectedText);
  if (!searchText) {
    return false;
  }
  const textAtRange = getDocumentPlainText(editor.state.doc, range.from, range.to);
  return textsEqualForSearch(textAtRange, searchText);
};

/**
 * Resolves a comment's document range using, in order:
 * 1. An existing highlight mark tagged with `commentId` whose text still matches `selectedText`.
 * 2. Stored `fromPos` / `toPos` when the text there still matches `selectedText`.
 * 3. Nearest `selectedText` rematch against stored `fromPos` / `toPos` (single-pass; no distance cap).
 * Whitespace-only anchors rematch the same whitespace character near the stored position.
 */
export const findCommentTextPosition = (
  editor: Editor | null,
  comment: {
    id: string;
    fromPos: number;
    toPos: number;
    selectedText?: string;
    fromRel?: Record<string, unknown> | null;
    toRel?: Record<string, unknown> | null;
  },
  ydoc?: Y.Doc | null,
): TextPosition | null => {
  if (!editor) return null;

  const markPosition = findCommentMarkPosition(editor, comment.id);
  if (markPosition && commentSelectedTextMatchesRange(editor, markPosition, comment.selectedText)) {
    return markPosition;
  }

  const relativeRange = resolveDocAnchor(editor, ydoc, {
    fromRel: comment.fromRel ?? null,
    toRel: comment.toRel ?? null,
  });
  if (
    relativeRange
    && relativeRange.from < relativeRange.to
    && (
      !comment.selectedText
      || commentSelectedTextMatchesRange(editor, relativeRange, comment.selectedText)
    )
  ) {
    return relativeRange;
  }

  const searchText = getCommentSearchText(comment.selectedText);
  const docSize = editor.state.doc.content.size;
  if (
    searchText
    && comment.fromPos >= 0
    && comment.toPos <= docSize
    && comment.fromPos < comment.toPos
    && commentSelectedTextMatchesRange(
      editor,
      { from: comment.fromPos, to: comment.toPos },
      comment.selectedText,
    )
  ) {
    return { from: comment.fromPos, to: comment.toPos };
  }

  const recalculated = recalculateCommentPosition(editor, {
    id: comment.id,
    fromPos: comment.fromPos,
    toPos: comment.toPos,
    selectedText: comment.selectedText ?? '',
    fromRel: comment.fromRel,
    toRel: comment.toRel,
  }, ydoc);

  if (
    recalculated.fromPos < 0
    || recalculated.toPos > docSize
    || recalculated.fromPos >= recalculated.toPos
  ) {
    return null;
  }

  if (!searchText) {
    return { from: recalculated.fromPos, to: recalculated.toPos };
  }

  if (
    commentSelectedTextMatchesRange(
      editor,
      { from: recalculated.fromPos, to: recalculated.toPos },
      comment.selectedText,
    )
  ) {
    return { from: recalculated.fromPos, to: recalculated.toPos };
  }

  return null;
};

export const recalculateCommentPosition = (
  editor: Editor | null,
  comment: CommentWithSelectedText,
  ydoc?: Y.Doc | null,
): { fromPos: number; toPos: number } => {
  if (!editor) {
    return { fromPos: comment.fromPos, toPos: comment.toPos };
  }

  const relativeRange = resolveDocAnchor(editor, ydoc, {
    fromRel: comment.fromRel ?? null,
    toRel: comment.toRel ?? null,
  });
  if (
    relativeRange
    && relativeRange.from < relativeRange.to
    && (
      !comment.selectedText
      || commentSelectedTextMatchesRange(
        editor,
        relativeRange,
        comment.selectedText,
      )
    )
  ) {
    return { fromPos: relativeRange.from, toPos: relativeRange.to };
  }

  if (!comment.selectedText) {
    return { fromPos: comment.fromPos, toPos: comment.toPos };
  }

  const searchText = getCommentSearchText(comment.selectedText);
  if (searchText.length === 0) {
    return { fromPos: comment.fromPos, toPos: comment.toPos };
  }

  const docSize = editor.state.doc.content.size;
  if (
    comment.fromPos >= 0
    && comment.toPos <= docSize
    && comment.fromPos < comment.toPos
    && commentSelectedTextMatchesRange(
      editor,
      { from: comment.fromPos, to: comment.toPos },
      comment.selectedText,
    )
  ) {
    return { fromPos: comment.fromPos, toPos: comment.toPos };
  }

  const nearest = findNearestResolvedTextPositionInDoc(
    editor.state.doc,
    searchText,
    comment.fromPos,
  );
  if (!nearest) {
    return { fromPos: comment.fromPos, toPos: comment.toPos };
  }
  return { fromPos: nearest.from, toPos: nearest.to };
};

type CommentForHighlight = {
  id: string;
  fromPos: number;
  toPos: number;
  selectedText?: string;
};

export const highlightComments = (
  editor: Editor | null,
  comments: CommentForHighlight[],
  options?: { skipIfReadOnly?: boolean; skipIfFocusMode?: boolean; isReadOnly?: boolean; isFocusMode?: boolean }
): void => {
  if (!editor) return;
  if (options?.skipIfReadOnly && options?.isReadOnly) return;
  if (options?.skipIfFocusMode && options?.isFocusMode) return;

  const { state } = editor;
  const markType = state.schema.marks.highlightColor;
  if (!markType) return;

  const existingRangesByCommentId = new Map<string, Array<{ from: number; to: number; mark: Mark }>>();
  forEachInlineHighlightMark(state.doc, (node, pos, mark) => {
    if (!mark.attrs.commentId) {
      return;
    }
    const commentId = String(mark.attrs.commentId);
    const ranges = existingRangesByCommentId.get(commentId) ?? [];
    ranges.push({ from: pos, to: pos + node.nodeSize, mark });
    existingRangesByCommentId.set(commentId, ranges);
  });

  const desiredByCommentId = new Map<string, { from: number; to: number }>();
  comments.forEach((comment) => {
    try {
      const existingRanges = existingRangesByCommentId.get(comment.id);
      if (existingRanges && existingRanges.length > 0) {
        const markPosition = {
          from: Math.min(...existingRanges.map((range) => range.from)),
          to: Math.max(...existingRanges.map((range) => range.to)),
        };
        if (commentSelectedTextMatchesRange(editor, markPosition, comment.selectedText)) {
          desiredByCommentId.set(comment.id, markPosition);
          return;
        }
      }
      const position = findCommentTextPosition(editor, comment);
      if (position) {
        desiredByCommentId.set(comment.id, position);
      }
    } catch (error) {
      console.warn('Error recalculating comment position:', error, comment);
    }
  });

  const mergedExistingByCommentId = new Map<string, { from: number; to: number }>();
  existingRangesByCommentId.forEach((ranges, commentId) => {
    mergedExistingByCommentId.set(commentId, {
      from: Math.min(...ranges.map((range) => range.from)),
      to: Math.max(...ranges.map((range) => range.to)),
    });
  });

  const commentIdsToRewrite = new Set<string>();
  desiredByCommentId.forEach((desired, commentId) => {
    const existing = mergedExistingByCommentId.get(commentId);
    if (!existing || existing.from !== desired.from || existing.to !== desired.to) {
      commentIdsToRewrite.add(commentId);
    }
  });
  existingRangesByCommentId.forEach((_ranges, commentId) => {
    if (!desiredByCommentId.has(commentId)) {
      commentIdsToRewrite.add(commentId);
    }
  });

  if (commentIdsToRewrite.size === 0) {
    return;
  }

  const tr = state.tr;
  commentIdsToRewrite.forEach((commentId) => {
    const existingRanges = existingRangesByCommentId.get(commentId);
    existingRanges?.forEach(({ from, to, mark }) => {
      tr.removeMark(from, to, mark);
    });
    const desired = desiredByCommentId.get(commentId);
    if (!desired) {
      return;
    }
    const mark = markType.create({
      color: COMMENT_HIGHLIGHT_COLOR,
      commentId,
    });
    tr.addMark(desired.from, desired.to, mark);
  });

  if (tr.docChanged) {
    preserveScrollDuring(editor, () => {
      editor.view.dispatch(tr);
    });
  }
};

export const clearSearchHighlights = (editor: Editor | null): void => {
  if (!editor) return;
  const tr = editor.state.tr;
  clearSearchMatchHighlights(tr);
  editor.view.dispatch(tr);
};

type CommentForScrolling = {
  id: string;
  fromPos: number;
  toPos: number;
  selectedText?: string;
};

export type ScrollToCommentResult = {
  foundExact: boolean;
  scrolled: boolean;
};

export const resolveClosestStoredCommentPosition = (
  editor: Editor,
  comment: { fromPos: number; toPos: number },
): { from: number; to: number } | null => {
  if (!Number.isFinite(comment.fromPos)) {
    return null;
  }

  const doc = editor.state.doc;
  const docSize = doc.content.size;
  if (docSize < 1) {
    return null;
  }

  if (Number.isFinite(comment.toPos)) {
    const clamped = clampDocRange(doc, comment.fromPos, comment.toPos);
    if (clamped) {
      return clamped;
    }
  }

  const rawPos = Math.max(0, Math.min(Math.floor(comment.fromPos), docSize));
  try {
    const selection = TextSelection.near(doc.resolve(rawPos));
    return { from: selection.from, to: selection.from };
  } catch {
    return null;
  }
};

const ensureCommentMarkInDocument = (
  editor: Editor,
  comment: CommentForScrolling,
): HTMLElement | null => {
  if (editor.isDestroyed) return null;

  const editorRoot = editor.view.dom as HTMLElement;
  const existingMark = editorRoot.querySelector(
    `[data-comment-id="${CSS.escape(comment.id)}"]`,
  ) as HTMLElement | null;
  if (existingMark) {
    return existingMark;
  }

  const markType = editor.state.schema.marks.highlightColor;
  if (!markType) {
    return null;
  }

  const position = findCommentTextPosition(editor, comment);
  if (!position) {
    return null;
  }

  const tr = editor.state.tr.addMark(
    position.from,
    position.to,
    markType.create({
      color: COMMENT_HIGHLIGHT_COLOR,
      commentId: comment.id,
    }),
  );
  editor.view.dispatch(tr);

  return editorRoot.querySelector(
    `[data-comment-id="${CSS.escape(comment.id)}"]`,
  ) as HTMLElement | null;
};

const focusEditorOnRange = (
  editor: Editor,
  fromPos: number,
  toPos: number,
): void => {
  const selectionFrom = Math.min(fromPos, toPos);
  const selectionTo = Math.max(fromPos, toPos);

  try {
    if (selectionFrom === selectionTo) {
      editor.commands.setTextSelection(selectionFrom);
    } else {
      editor.commands.setTextSelection({ from: selectionFrom, to: selectionTo });
    }
  } catch {
    try {
      const selection = TextSelection.near(editor.state.doc.resolve(selectionFrom));
      editor.view.dispatch(editor.state.tr.setSelection(selection));
    } catch {
      return;
    }
  }

  editor.commands.focus();
};

const scrollEditorToDocPos = (
  editor: Editor,
  fromPos: number,
  scrollContainer: HTMLElement | null,
): void => {
  const { view } = editor;
  const maxPos = editor.state.doc.content.size;
  const safePos = Math.max(0, Math.min(fromPos, maxPos));

  try {
    const domAtPos = view.domAtPos(safePos);
    if (!domAtPos.node) {
      return;
    }

    let targetNode: Node | null = domAtPos.node;
    if (targetNode.nodeType === Node.TEXT_NODE) {
      const textNode = targetNode as Text;
      const range = document.createRange();
      range.setStart(textNode, domAtPos.offset);
      range.setEnd(textNode, Math.min(domAtPos.offset + 1, textNode.textContent?.length || 0));

      if (typeof range.getBoundingClientRect !== 'function') {
        range.detach();
        return;
      }

      const rect = range.getBoundingClientRect();
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop
          + (rect.top - containerRect.top)
          - (scrollContainer.clientHeight / 2)
          + (rect.height / 2);

        scrollContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth',
        });
      } else if (typeof view.dom.scrollIntoView === 'function') {
        view.dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      range.detach();
      return;
    }

    const element = targetNode as HTMLElement;
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  } catch {
    return;
  }
};

export const scrollToComment = (
  editor: Editor | null,
  comment: CommentForScrolling,
  scrollContainerRef?: React.RefObject<HTMLElement>
): ScrollToCommentResult => {
  if (!editor || editor.isDestroyed) {
    return { foundExact: false, scrolled: false };
  }

  const exactPosition = findCommentTextPosition(editor, comment);
  const position = exactPosition ?? resolveClosestStoredCommentPosition(editor, comment);
  if (!position) {
    return { foundExact: false, scrolled: false };
  }

  const foundExact = exactPosition !== null;
  const { from: fromPos, to: toPos } = position;

  focusEditorOnRange(editor, fromPos, toPos);

  const { view } = editor;
  const editorRoot = view.dom as HTMLElement;
  const scrollContainer = scrollContainerRef?.current || findScrollContainer(editorRoot.parentElement);

  clearMarkPulses(editorRoot);
  clearCommentLocationIndicators();

  const revealComment = (): void => {
    if (editor.isDestroyed) return;

    if (foundExact) {
      const markElement = ensureCommentMarkInDocument(editor, {
        ...comment,
        fromPos,
        toPos,
      });
      if (markElement && isMarkElementVisuallyHighlightable(markElement)) {
        scrollElementToContainerCenter(markElement, scrollContainer);
        pulseMarkElement(markElement);
        return;
      }
      if (markElement) {
        scrollElementToContainerCenter(markElement, scrollContainer);
        pulseMarkElement(markElement);
      }
    }

    scrollEditorToDocPos(editor, fromPos, scrollContainer);
    showTemporaryCommentLocationIndicator(editor, fromPos, scrollContainer);
  };

  window.requestAnimationFrame(revealComment);
  return { foundExact, scrolled: true };
};

export const scrollCaretToFractionFromTop = (
  editor: Editor | null,
  pos: number,
  scrollContainer: HTMLElement | null,
  fractionFromTop: number = 0.70
): void => {
  if (!editor || !scrollContainer) return;
  const { view } = editor;
  const coords = view.coordsAtPos(pos);
  const containerRect = scrollContainer.getBoundingClientRect();
  const caretOffsetFromTopOfVisible = coords.top - containerRect.top;
  const targetOffset = scrollContainer.clientHeight * fractionFromTop;
  const scrollDelta = caretOffsetFromTopOfVisible - targetOffset;
  const newScrollTop = scrollContainer.scrollTop + scrollDelta;
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
  scrollContainer.scrollTo({
    top: Math.max(0, Math.min(newScrollTop, maxScrollTop)),
    behavior: 'auto'
  });
};

export const isEditorScrollContainerReadyForRestore = (
  scrollContainer: HTMLElement,
  state: ReadingState
): boolean => {
  if (state.scrollTop <= 0) return true;
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
  return maxScrollTop >= state.scrollTop;
};

export const deferEditorCommand = (callback: () => void): void => {
  queueMicrotask(() => {
    requestAnimationFrame(callback);
  });
};

export const DIALOG_EXIT_ANIMATION_MS = 250;

export const waitForDialogExit = (): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, DIALOG_EXIT_ANIMATION_MS);
  });

export const deferAfterDialogExit = (callback: () => void): void => {
  void waitForDialogExit().then(() => {
    deferEditorCommand(callback);
  });
};

export const applyEditorScrollState = (
  editor: Editor,
  scrollContainer: HTMLElement,
  state: ReadingState,
  fractionFromTop: number = 0.70
): void => {
  const maxPos = editor.state.doc.content.size;
  const pos = Math.min(Math.max(1, state.pos), maxPos);

  const applyScroll = (): void => {
    if (state.scrollTop > 0) {
      const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      scrollContainer.scrollTop = Math.min(state.scrollTop, maxScrollTop);
    } else if (pos > 1) {
      scrollCaretToFractionFromTop(editor, pos, scrollContainer, fractionFromTop);
    }
  };

  applyScroll();
  editor.commands.setTextSelection(pos);
  applyScroll();
  requestAnimationFrame(applyScroll);
};

export const convertTextWithNewlinesToParagraphs = (text: string): Array<{ type: string; content: Array<{ type: string; text: string }> }> => {
  if (!text.includes('\n')) {
    return [{ type: 'paragraph', content: [{ type: 'text', text }] }];
  }
  
  const parts = text.split('\n');
  return parts.map((part) => ({
    type: 'paragraph',
    content: part ? [{ type: 'text', text: part }] : [{ type: 'text', text: '' }]
  }));
};

const MARKS_EXCLUDED_FROM_PRESERVATION = new Set(['highlightColor']);

const collectCharacterMarks = (
  doc: ProseMirrorNode,
  from: number,
  to: number
): Array<readonly Mark[]> => {
  const marksPerChar: Array<readonly Mark[]> = [];

  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText && node.text) {
      const start = Math.max(from, pos);
      const end = Math.min(to, pos + node.text.length);
      const preservedMarks = node.marks.filter(
        (mark) => !MARKS_EXCLUDED_FROM_PRESERVATION.has(mark.type.name)
      );

      for (let i = start; i < end; i++) {
        marksPerChar.push(preservedMarks);
      }
      return;
    }

    if (node.type.name === 'hardBreak' && pos >= from && pos < to) {
      marksPerChar.push([]);
      return;
    }

    if (isMentionNode(node) && pos >= from && pos < to) {
      const mentionText = getLeafNodePlainText(node);
      for (let i = 0; i < mentionText.length; i++) {
        marksPerChar.push([]);
      }
    }
  });

  return marksPerChar;
};

const marksFromInlineMarkdown = (
  schema: Schema,
  marks: InlineMarkdownMarks,
): Mark[] => {
  const result: Mark[] = [];
  if (marks.bold && schema.marks.bold) {
    result.push(schema.marks.bold.create());
  }
  if (marks.italic && schema.marks.italic) {
    result.push(schema.marks.italic.create());
  }
  if (marks.strike && schema.marks.strike) {
    result.push(schema.marks.strike.create());
  }
  return result;
};

const buildNodesFromInlineMarkdown = (
  schema: Schema,
  suggestionText: string,
): ProseMirrorNode[] => {
  const nodes: ProseMirrorNode[] = [];
  for (const segment of parseInlineMarkdown(suggestionText)) {
    if (!segment.text) {
      continue;
    }
    nodes.push(schema.text(segment.text, marksFromInlineMarkdown(schema, segment.marks)));
  }
  return nodes;
};

const buildMarkedTextNodes = (
  schema: Schema,
  originalText: string,
  originalMarks: Array<readonly Mark[]>,
  text: string,
): ProseMirrorNode[] => {
  if (!text) {
    return [];
  }

  if (hasInlineMarkdownMarkers(text)) {
    return buildNodesFromInlineMarkdown(schema, text);
  }

  if (originalMarks.length === 0) {
    return [schema.text(text)];
  }

  const firstMarks = originalMarks[0]!;
  const allMarksUniform = originalMarks.every((marks) => Mark.sameSet(marks, firstMarks));
  if (allMarksUniform) {
    return [schema.text(text, [...firstMarks])];
  }

  const alignment = buildEditAlignment(originalText, text);
  const suggestionMarks: Array<readonly Mark[]> = Array.from(
    { length: text.length },
    () => []
  );

  let fallbackOriginalIndex = 0;
  for (const step of alignment) {
    if (step.originalIndex !== null) {
      fallbackOriginalIndex = step.originalIndex;
    }
    if (step.suggestionIndex === null) {
      continue;
    }
    suggestionMarks[step.suggestionIndex] = nearestOriginalMarks(
      originalMarks,
      step.originalIndex,
      fallbackOriginalIndex
    );
  }

  const nodes: ProseMirrorNode[] = [];
  let buffer = '';
  let bufferMarks: readonly Mark[] = suggestionMarks[0] ?? [];

  const flushBuffer = (): void => {
    if (!buffer) {
      return;
    }
    nodes.push(schema.text(buffer, [...bufferMarks]));
    buffer = '';
  };

  for (let index = 0; index < text.length; index++) {
    const marks = suggestionMarks[index] ?? [];
    if (buffer.length > 0 && !Mark.sameSet(marks, bufferMarks)) {
      flushBuffer();
      bufferMarks = marks;
    } else if (buffer.length === 0) {
      bufferMarks = marks;
    }
    buffer += text[index];
  }
  flushBuffer();

  return nodes;
};

const buildEditAlignment = (
  originalText: string,
  suggestionText: string
): Array<{ originalIndex: number | null; suggestionIndex: number | null }> => {
  const originalLength = originalText.length;
  const suggestionLength = suggestionText.length;
  const distances: number[][] = Array.from({ length: originalLength + 1 }, () =>
    Array.from({ length: suggestionLength + 1 }, () => 0)
  );

  for (let i = 0; i <= originalLength; i++) {
    distances[i]![0] = i;
  }
  for (let j = 0; j <= suggestionLength; j++) {
    distances[0]![j] = j;
  }

  for (let i = 1; i <= originalLength; i++) {
    for (let j = 1; j <= suggestionLength; j++) {
      const substitutionCost = originalText[i - 1] === suggestionText[j - 1] ? 0 : 1;
      distances[i]![j] = Math.min(
        distances[i - 1]![j]! + 1,
        distances[i]![j - 1]! + 1,
        distances[i - 1]![j - 1]! + substitutionCost
      );
    }
  }

  const alignment: Array<{ originalIndex: number | null; suggestionIndex: number | null }> = [];
  let i = originalLength;
  let j = suggestionLength;

  while (i > 0 || j > 0) {
    if (
      i > 0
      && j > 0
      && distances[i]![j] === distances[i - 1]![j - 1]! + (originalText[i - 1] === suggestionText[j - 1] ? 0 : 1)
    ) {
      alignment.push({ originalIndex: i - 1, suggestionIndex: j - 1 });
      i -= 1;
      j -= 1;
    } else if (j > 0 && distances[i]![j] === distances[i]![j - 1]! + 1) {
      alignment.push({ originalIndex: null, suggestionIndex: j - 1 });
      j -= 1;
    } else {
      alignment.push({ originalIndex: i - 1, suggestionIndex: null });
      i -= 1;
    }
  }

  return alignment.reverse();
};

const nearestOriginalMarks = (
  originalMarks: Array<readonly Mark[]>,
  originalIndex: number | null,
  fallbackIndex: number
): readonly Mark[] => {
  if (originalIndex !== null && originalMarks[originalIndex]) {
    return originalMarks[originalIndex]!;
  }

  for (let offset = 0; offset < originalMarks.length; offset++) {
    const left = fallbackIndex - offset;
    const right = fallbackIndex + offset;
    if (left >= 0 && originalMarks[left]) {
      return originalMarks[left]!;
    }
    if (right < originalMarks.length && originalMarks[right]) {
      return originalMarks[right]!;
    }
  }

  return [];
};

export const buildSuggestionContentPreservingMarks = (
  doc: ProseMirrorNode,
  from: number,
  to: number,
  suggestionText: string
): ProseMirrorNode[] => {
  const { schema } = doc.type;
  if (!suggestionText) {
    return [];
  }

  if (hasInlineMarkdownMarkers(suggestionText)) {
    return buildNodesFromInlineMarkdown(schema, suggestionText);
  }

  const originalText = getDocumentPlainText(doc, from, to);
  const originalMarks = collectCharacterMarks(doc, from, to);
  const mentions = collectMentionsInRange(doc, from, to);
  const segments = segmentSuggestionTextWithMentions(suggestionText, mentions);
  const hasSurvivingMentions = segments.some((segment) => segment.type === 'mention');

  if (!hasSurvivingMentions) {
    return buildMarkedTextNodes(schema, originalText, originalMarks, suggestionText);
  }

  const nodes: ProseMirrorNode[] = [];
  for (const segment of segments) {
    if (segment.type === 'mention') {
      nodes.push(segment.node.type.create(segment.node.attrs));
      continue;
    }
    nodes.push(...buildMarkedTextNodes(schema, originalText, originalMarks, segment.text));
  }
  return nodes;
};

export const replaceRangePreservingMarks = (
  editor: Editor,
  from: number,
  to: number,
  suggestionText: string
): boolean => {
  const { state } = editor;
  const originalText = getDocumentPlainText(state.doc, from, to);
  const spansMultipleParagraphs = originalText.includes('\n');
  const suggestionHasNewlines = suggestionText.includes('\n');
  const suggestionHasMarkdown = hasInlineMarkdownMarkers(suggestionText);
  const mentions = collectMentionsInRange(state.doc, from, to);
  const hasSurvivingMentions = segmentSuggestionTextWithMentions(
    suggestionText,
    mentions,
  ).some((segment) => segment.type === 'mention');

  if (
    spansMultipleParagraphs
    && suggestionHasNewlines
    && !hasSurvivingMentions
    && !suggestionHasMarkdown
  ) {
    return editor.chain()
      .setTextSelection({ from, to })
      .insertContent(convertTextWithNewlinesToParagraphs(suggestionText))
      .setTextSelection({ from, to: from })
      .focus()
      .run();
  }

  const content = buildSuggestionContentPreservingMarks(state.doc, from, to, suggestionText);
  const tr = state.tr.replaceWith(from, to, content);
  tr.setSelection(TextSelection.create(tr.doc, from));
  editor.view.dispatch(tr);
  editor.commands.focus();
  return true;
};

